import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import {
    MatDialogRef,
    MatDialogModule,
    MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { ControlDusService } from '../../../services/control-dus.service';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { LoggerService } from '../../../core/services/logger.service';
import { inject } from '@angular/core';

export interface DocumentosDialogData {
    nroIE: number;
    numeroNroIE: string;
    dus?: string;
    contenedor?: string;
}

export interface ArchivoControlDus {
    id: number;
    nroIE: number;
    nombre: string;
    tipo: string;
    documentNumber?: string;
    downloadUrl?: string;
    fileSize?: string;
    extension?: string;
    comentarios?: string;
    creadoPor: string;
    fechaCreacion: string;
    lastUpdateDate?: string;
    lastUpdateBy?: string;
}

@Component({
    selector: 'app-documentos-control-dus-dialog',
    templateUrl: './documentos-control-dus-dialog.component.html',
    styleUrls: ['./documentos-control-dus-dialog.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        MatTableModule,
        MatChipsModule,
        MatTooltipModule,
        MatProgressSpinnerModule,
        MatSnackBarModule,
        MatCardModule,
        MatCheckboxModule,
    ],
    providers: [LoggerService],
})
export class DocumentosControlDusDialogComponent implements OnInit {
    // Lista de archivos
    archivos: ArchivoControlDus[] = [];

    // Estado de carga
    isLoading = false;

    // Selección de archivos para descarga
    selectedFileIds = new Set<number>();

    private logger = inject(LoggerService);
    private snackBar = inject(MatSnackBar);
    private controlDusService = inject(ControlDusService);

    /**
     * Formatea el número de nroIE eliminando ceros a la izquierda
     */
    get numeroNroIE(): string {
        return this.data.numeroNroIE.replace(/^0+/, '');
    }

    constructor(
        // eslint-disable-next-line no-unused-vars
        private dialogRef: MatDialogRef<DocumentosControlDusDialogComponent>,
        // eslint-disable-next-line no-unused-vars
        @Inject(MAT_DIALOG_DATA) public data: DocumentosDialogData
    ) {}

    ngOnInit(): void {
        this.cargarArchivos();
    }

    /**
     * Formatea una fecha al formato dd-mm-aaaa
     */
    private formatearFecha(fecha: string): string {
        const date = new Date(fecha);
        const dia = date.getDate().toString().padStart(2, '0');
        const mes = (date.getMonth() + 1).toString().padStart(2, '0');
        const anio = date.getFullYear();
        return `${dia}-${mes}-${anio}`;
    }

    /**
     * Extrae el mensaje de error del backend desde diferentes formatos
     * Maneja casos donde el backend responde JSON pero Angular lo recibe como Blob
     */
    private async extraerMensajeError(
        unknownError: unknown
    ): Promise<string | null> {
        const maybeHttpError: any = unknownError as any;
        if (!maybeHttpError) return null;

        // Intentar leer mensaje desde e.error
        const rawError = maybeHttpError.error ?? maybeHttpError;

        try {
            // Caso: Blob (por responseType: 'blob')
            if (rawError instanceof Blob) {
                const text = await rawError.text();
                try {
                    const parsed = JSON.parse(text);
                    if (
                        parsed &&
                        typeof parsed.message === 'string' &&
                        parsed.message.trim()
                    ) {
                        return parsed.message;
                    }
                    if (
                        parsed &&
                        typeof parsed.error === 'string' &&
                        parsed.error.trim()
                    ) {
                        return parsed.error;
                    }
                } catch {
                    // Si no se puede parsear JSON, continuar a otras estrategias
                }
            }

            // Caso: objeto con message
            if (
                rawError &&
                typeof rawError === 'object' &&
                'message' in rawError &&
                typeof rawError.message === 'string'
            ) {
                return rawError.message as string;
            }

            // Caso: string (intentar parsear o retornar directo)
            if (typeof rawError === 'string') {
                try {
                    const parsed = JSON.parse(rawError);
                    if (parsed && typeof parsed.message === 'string')
                        return parsed.message;
                } catch {
                    return rawError;
                }
            }
        } catch {
            // Si todo falla, retornar null y se usará un mensaje genérico
        }

        return null;
    }

    async cargarArchivos(): Promise<void> {
        try {
            this.isLoading = true;
            const response = await firstValueFrom(
                this.controlDusService.getDocumentosPdfControlDus(
                    this.data.nroIE,
                    this.data.contenedor
                )
            );

            // Mapear la respuesta del servicio según el payload proporcionado
            if (response && response.files && Array.isArray(response.files)) {
                this.archivos = response.files.map((doc: any) => ({
                    id: doc.id || 0,
                    nroIE: doc.nroIE || this.data.nroIE,
                    nombre: doc.fileName || 'N/D',
                    tipo: doc.fileType || 'N/D',
                    documentNumber: doc.documentNumber || undefined,
                    downloadUrl: doc.downloadUrl || undefined,
                    fileSize: doc.fileSize || undefined,
                    extension: doc.extension || undefined,
                    comentarios: doc.remarks || '',
                    creadoPor: doc.createdBy || 'N/D',
                    fechaCreacion: this.formatearFecha(doc.creationDate),
                    lastUpdateDate: doc.lastUpdateDate
                        ? this.formatearFecha(doc.lastUpdateDate)
                        : undefined,
                    lastUpdateBy: doc.lastUpdateBy || undefined,
                }));

                // Seleccionar todos por defecto
                this.selectedFileIds.clear();
                this.archivos.forEach((a) => this.selectedFileIds.add(a.id));

                this.logger.info(
                    `✅ Documentos cargados: ${this.archivos.length} archivos para nroIE ${this.data.nroIE}`,
                    'DocumentosControlDusDialogComponent'
                );
            } else {
                this.archivos = [];
                this.logger.warn(
                    `⚠️ No se encontraron archivos en la respuesta para nroIE ${this.data.nroIE}`,
                    'DocumentosControlDusDialogComponent'
                );
            }
        } catch (error) {
            this.logger.error(
                'Error al cargar archivos:',
                error as Error,
                'DocumentosControlDusDialogComponent'
            );
            this.snackBar.open('Error al cargar los archivos', 'Cerrar', {
                duration: 5000,
            });
            this.archivos = [];
        } finally {
            this.isLoading = false;
        }
    }

    /**
     * Valida que el blob recibido sea una descarga de archivo y no un error del API.
     */
    private isBlobDescargaValido(blob: Blob): boolean {
        if (blob.size === 0) {
            return false;
        }
        const tipo = blob.type.toLowerCase();
        if (
            tipo === 'application/json' ||
            tipo.startsWith('text/html') ||
            tipo.startsWith('text/plain')
        ) {
            return false;
        }
        return true;
    }

    /**
     * Descarga un archivo individual (PDF, Excel u otros tipos soportados).
     */
    async descargarArchivo(archivo: ArchivoControlDus): Promise<void> {
        try {
            this.isLoading = true;
            const response: Blob = await firstValueFrom(
                this.controlDusService.descargarDocumentoPdf(
                    this.data.nroIE,
                    archivo.id
                )
            );

            if (!this.isBlobDescargaValido(response)) {
                throw new Error('ARCHIVO_NO_ENCONTRADO');
            }

            // Crear un objeto URL para el blob
            const url = window.URL.createObjectURL(response);

            // Crear un elemento <a> temporal
            const link = document.createElement('a');
            link.href = url;
            link.download = archivo.nombre; // Usar el nombre original del archivo

            // Simular clic para descargar
            document.body.appendChild(link);
            link.click();

            // Limpiar
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            this.snackBar.open('Archivo descargado exitosamente', 'Cerrar', {
                duration: 3000,
            });
        } catch (error) {
            this.logger.error('Error al descargar archivo:', error as Error);
            if ((error as Error).message === 'ARCHIVO_NO_ENCONTRADO') {
                this.snackBar.open(
                    'El archivo solicitado no se encuentra disponible',
                    'Cerrar',
                    { duration: 5000 }
                );
            } else {
                this.snackBar.open('Error al descargar el archivo', 'Cerrar', {
                    duration: 5000,
                });
            }
        } finally {
            this.isLoading = false;
        }
    }

    /**
     * Alterna la selección de un archivo individual
     */
    toggleSelection(archivo: ArchivoControlDus): void {
        if (this.selectedFileIds.has(archivo.id)) {
            this.selectedFileIds.delete(archivo.id);
        } else {
            this.selectedFileIds.add(archivo.id);
        }
    }

    /**
     * Alterna seleccionar/deseleccionar todos los archivos
     */
    toggleSelectAll(): void {
        if (this.isAllSelected()) {
            this.selectedFileIds.clear();
        } else {
            this.archivos.forEach((a) => this.selectedFileIds.add(a.id));
        }
    }

    /**
     * Verifica si todos los archivos están seleccionados
     */
    isAllSelected(): boolean {
        return (
            this.archivos.length > 0 &&
            this.selectedFileIds.size === this.archivos.length
        );
    }

    /**
     * Verifica si hay una selección parcial (indeterminate)
     */
    isIndeterminate(): boolean {
        return (
            this.selectedFileIds.size > 0 &&
            this.selectedFileIds.size < this.archivos.length
        );
    }

    /**
     * Archivos actualmente marcados con el checkbox.
     */
    getArchivosSeleccionados(): ArchivoControlDus[] {
        return this.archivos.filter((a) => this.selectedFileIds.has(a.id));
    }

    /**
     * Indica si la selección incluye al menos un archivo Excel.
     */
    tieneExcelEnSeleccion(): boolean {
        return this.getArchivosSeleccionados().some((a) =>
            this.isExcelFile(a)
        );
    }

    /**
     * La descarga consolidada solo aplica cuando hay selección y todos son PDF.
     */
    puedeDescargarSeleccionadosConsolidado(): boolean {
        const seleccionados = this.getArchivosSeleccionados();
        return (
            seleccionados.length > 0 &&
            seleccionados.every((a) => this.isPdfFile(a))
        );
    }

    /**
     * Mensaje informativo cuando la descarga consolidada no está disponible.
     */
    getMensajeDescargaConsolidada(): string | null {
        if (this.selectedFileIds.size === 0) {
            return null;
        }
        if (this.tieneExcelEnSeleccion()) {
            return 'Los archivos Excel se descargan de forma individual. Use el botón de descarga en cada fila.';
        }
        if (!this.puedeDescargarSeleccionadosConsolidado()) {
            return 'La descarga consolidada solo está disponible para archivos PDF.';
        }
        return null;
    }

    /**
     * Descarga los documentos seleccionados en un solo archivo PDF.
     * Si todos están seleccionados, usa el endpoint de merge completo.
     * Si hay selección parcial, usa el endpoint de merge selectivo.
     */
    async descargarSeleccionados(): Promise<void> {
        if (this.selectedFileIds.size === 0) {
            this.snackBar.open(
                'Seleccione al menos un documento para descargar',
                'Cerrar',
                { duration: 3000 }
            );
            return;
        }

        if (!this.puedeDescargarSeleccionadosConsolidado()) {
            const mensaje =
                this.getMensajeDescargaConsolidada() ??
                'La descarga consolidada solo está disponible para archivos PDF.';
            this.snackBar.open(mensaje, 'Cerrar', { duration: 5000 });
            return;
        }

        try {
            this.isLoading = true;
            const fileIds = Array.from(this.selectedFileIds);
            this.logger.info(
                `📥 Iniciando descarga de ${fileIds.length} documentos para nroIE: ${this.data.nroIE}`,
                'DocumentosControlDusDialogComponent'
            );

            let response: Blob;
            if (this.isAllSelected()) {
                response = await firstValueFrom(
                    this.controlDusService.descargarTodosDocumentosPdf(
                        this.data.nroIE
                    )
                );
            } else {
                response = await firstValueFrom(
                    this.controlDusService.descargarDocumentosSeleccionados(
                        this.data.nroIE,
                        fileIds
                    )
                );
            }

            // Verificar si el blob está vacío o no es un PDF válido
            if (response.size === 0) {
                throw new Error('El archivo PDF consolidado está vacío');
            }

            // Verificar el tipo MIME (puede ser application/pdf o application/json si hay error)
            if (response.type === 'application/json') {
                // El backend retornó un error en formato JSON
                const errorText = await response.text();
                try {
                    const errorJson = JSON.parse(errorText);
                    if (
                        errorJson.fileErrors &&
                        Array.isArray(errorJson.fileErrors)
                    ) {
                        // Construir mensaje detallado con los archivos problemáticos
                        const archivosConError = errorJson.fileErrors
                            .map(
                                (err: any) =>
                                    `- ${err.fileName}: ${
                                        err.errorDescription || err.errorType
                                    }`
                            )
                            .join('\n');

                        const mensaje = `Error al procesar PDFs:\n${archivosConError}`;
                        this.logger.error(
                            `❌ Error al procesar PDFs: ${JSON.stringify(
                                errorJson
                            )}`,
                            undefined,
                            'DocumentosControlDusDialogComponent'
                        );
                        this.snackBar.open(mensaje, 'Cerrar', {
                            duration: 8000,
                            panelClass: ['error-snackbar'],
                        });
                        return;
                    }
                } catch {
                    // Si no se puede parsear, continuar con el manejo genérico
                }
                throw new Error(
                    errorText || 'Error desconocido al procesar PDFs'
                );
            }

            // Crear un objeto URL para el blob
            const url = window.URL.createObjectURL(response);

            // Crear un elemento <a> temporal
            const link = document.createElement('a');
            link.href = url;
            link.download = `DUS_${this.numeroNroIE}_consolidado.pdf`;

            // Simular clic para descargar
            document.body.appendChild(link);
            link.click();

            // Limpiar
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            this.logger.info(
                `✅ PDF consolidado descargado exitosamente para nroIE: ${this.data.nroIE}`,
                'DocumentosControlDusDialogComponent'
            );
            this.snackBar.open(
                'Documentos descargados exitosamente',
                'Cerrar',
                {
                    duration: 3000,
                    panelClass: ['success-snackbar'],
                }
            );
        } catch (error) {
            this.logger.error(
                'Error al descargar los documentos:',
                error as Error,
                'DocumentosControlDusDialogComponent'
            );

            let errorMessage = 'Error al descargar los documentos';

            // Verificar si el error tiene errorJson (viene del servicio mejorado)
            if ((error as any)?.errorJson) {
                const errorJson = (error as any).errorJson;
                if (errorJson.message) {
                    errorMessage = errorJson.message;
                }
                if (
                    errorJson.fileErrors &&
                    Array.isArray(errorJson.fileErrors)
                ) {
                    const archivosConError = errorJson.fileErrors
                        .map(
                            (err: any) =>
                                `- ${err.fileName}: ${
                                    err.errorDescription || err.errorType
                                }`
                        )
                        .join('\n');
                    errorMessage = `Error al procesar PDFs:\n${archivosConError}`;
                }
            } else {
                // Intentar extraer mensaje del backend usando el método existente
                const backendMessage = await this.extraerMensajeError(error);
                if (backendMessage) {
                    errorMessage = backendMessage;
                } else if ((error as any)?.error instanceof Blob) {
                    // Si el error es un Blob, intentar leerlo como JSON
                    try {
                        const errorText = await (
                            (error as any).error as Blob
                        ).text();
                        const errorJson = JSON.parse(errorText);
                        if (errorJson.message) {
                            errorMessage = errorJson.message;
                        }
                        if (
                            errorJson.fileErrors &&
                            Array.isArray(errorJson.fileErrors)
                        ) {
                            const archivosConError = errorJson.fileErrors
                                .map(
                                    (err: any) =>
                                        `- ${err.fileName}: ${
                                            err.errorDescription ||
                                            err.errorType
                                        }`
                                )
                                .join('\n');
                            errorMessage = `Error al procesar PDFs:\n${archivosConError}`;
                        }
                    } catch {
                        // Si no se puede parsear, usar mensaje genérico
                    }
                } else if ((error as any)?.message) {
                    // Usar el mensaje del error directamente
                    errorMessage = (error as any).message;
                }
            }

            this.logger.error(
                `❌ Error detallado al descargar PDF consolidado: ${errorMessage}`,
                error as Error,
                'DocumentosControlDusDialogComponent'
            );

            this.snackBar.open(errorMessage, 'Cerrar', {
                duration: 8000,
                panelClass: ['error-snackbar'],
            });
        } finally {
            this.isLoading = false;
        }
    }

    /**
     * Cierra el diálogo
     */
    cerrar(): void {
        this.dialogRef.close();
    }

    isPdfFile(archivo: ArchivoControlDus): boolean {
        return this.getFileExtension(archivo) === 'pdf';
    }

    isExcelFile(archivo: ArchivoControlDus): boolean {
        const ext = this.getFileExtension(archivo);
        return ['xls', 'xlsx', 'xlsm', 'csv'].includes(ext);
    }

    /**
     * Obtiene la extensión del archivo (campo extension o nombre de archivo).
     */
    getFileExtension(archivo: ArchivoControlDus): string {
        const fromField = archivo.extension?.trim().toLowerCase();
        if (fromField) {
            return fromField.replace(/^\./, '');
        }
        const match = archivo.nombre?.match(/\.([^.]+)$/i);
        return match ? match[1].toLowerCase() : '';
    }

    /**
     * Icono Material según tipo de archivo (PDF, Excel, Word, imagen, etc.).
     */
    getFileIcon(archivo: ArchivoControlDus): string {
        const ext = this.getFileExtension(archivo);
        switch (ext) {
            case 'pdf':
                return 'picture_as_pdf';
            case 'xls':
            case 'xlsx':
            case 'xlsm':
            case 'csv':
                return 'table_chart';
            case 'doc':
            case 'docx':
                return 'article';
            case 'png':
            case 'jpg':
            case 'jpeg':
            case 'gif':
            case 'webp':
            case 'bmp':
                return 'image';
            case 'zip':
            case 'rar':
            case '7z':
                return 'folder_zip';
            case 'txt':
                return 'text_snippet';
            default:
                return 'insert_drive_file';
        }
    }

    /**
     * Clase CSS para colorear el icono según el tipo de archivo.
     */
    getFileIconClass(archivo: ArchivoControlDus): string {
        const ext = this.getFileExtension(archivo);
        switch (ext) {
            case 'pdf':
                return 'file-icon--pdf';
            case 'xls':
            case 'xlsx':
            case 'xlsm':
            case 'csv':
                return 'file-icon--excel';
            case 'doc':
            case 'docx':
                return 'file-icon--word';
            case 'png':
            case 'jpg':
            case 'jpeg':
            case 'gif':
            case 'webp':
            case 'bmp':
                return 'file-icon--image';
            case 'zip':
            case 'rar':
            case '7z':
                return 'file-icon--archive';
            default:
                return 'file-icon--default';
        }
    }
}
